import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { getUiLang, setUiLang } from "../../utils/uiLanguage";
import copy from "../../features/home/homeCopy";
import eggImg from "../../assets/img/2 MAIN SCREENS EN/home/egg-4x.webp";
import eggWalletImg from "../../assets/img/2 MAIN SCREENS EN/home/egg-wallet.png";
import avatarFallback from "../../assets/img/figma/avatar-01.svg";

export const ga = {
  bg: "#0D0F0A",
  paper: "#15170F",
  lime: "#B8EE3C",
  gold: "#F9A825",
  muted: "#9AA88A",
  dim: "#666B5C",
  border: "#262B1C",
  text: "#F2FAE6",
  white: "#FFFFFF",
};

export const cutCorner = (size = 12) =>
  `polygon(0 0, calc(100% - ${size}px) 0, 100% ${size}px, 100% 100%, 0 100%)`;

export function useIsDesktop() {
  return useMediaQuery("(min-width:900px)");
}

export function getStoredSkillLevel() {
  return (sessionStorage.getItem("skillLevel") || "beginner").toLowerCase();
}

export function setStoredSkillLevel(level) {
  const next = String(level || "beginner").toLowerCase();
  sessionStorage.setItem("skillLevel", next);
  window.dispatchEvent(new Event("ga-skill-level"));
}

export function useSkillLevel() {
  const [skillLevel, setSkillLevel] = useState(getStoredSkillLevel);
  useEffect(() => {
    const onChange = () => setSkillLevel(getStoredSkillLevel());
    window.addEventListener("ga-skill-level", onChange);
    return () => window.removeEventListener("ga-skill-level", onChange);
  }, []);
  return skillLevel;
}

export function useUiLangState() {
  const [lang, setLang] = useState(getUiLang());
  useEffect(() => {
    const onChange = () => setLang(getUiLang());
    window.addEventListener("ga-ui-lang", onChange);
    return () => window.removeEventListener("ga-ui-lang", onChange);
  }, []);
  const switchLang = (next) => {
    setUiLang(next);
    setLang(next);
  };
  return { lang, switchLang, t: copy[lang] };
}

export function getEggBalance(profile) {
  const stored = sessionStorage.getItem("easterBalance");
  if (stored != null && stored !== "") {
    const storedCount = Number(stored);
    if (Number.isFinite(storedCount) && storedCount >= 0) return storedCount;
  }
  const raw =
    profile?.easters ??
    profile?.eggs ??
    profile?.easter_eggs ??
    profile?.easterEggs;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function getStreakCount(profile) {
  const raw =
    profile?.active_streak ??
    profile?.login_streak ??
    profile?.loginStreak ??
    profile?.daily_streak ??
    profile?.streak ??
    sessionStorage.getItem("loginStreak");
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function getRedemptionHistory() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem("redemptionHistory") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveRedemption(entry) {
  const history = getRedemptionHistory();
  const next = [
    {
      id: Date.now(),
      date: new Date().toISOString().slice(0, 10),
      status: "On the way",
      ...entry,
    },
    ...history,
  ].slice(0, 20);
  sessionStorage.setItem("redemptionHistory", JSON.stringify(next));
  return next;
}

export function PageShell({ children, wide }) {
  const isDesktop = useIsDesktop();
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        bgcolor: ga.bg,
        minHeight: "100%",
        pb: isDesktop ? 8 : 2,
        boxSizing: "border-box",
      }}
    >
      {children}
    </Box>
  );
}

export function SectionHead({ eyebrow, title, accent, subtitle }) {
  return (
    <Box sx={{ textAlign: "center", px: { xs: "16px", md: "20px" }, pt: { xs: 3, md: 4 }, pb: 1.5 }}>
      {eyebrow && (
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "6px",
            color: ga.lime,
            mb: 0.75,
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: { xs: 26, md: 32 },
            letterSpacing: "2px",
            color: ga.white,
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>
        {accent && (
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 26, md: 32 },
              letterSpacing: "2px",
              color: ga.lime,
              lineHeight: 1.1,
            }}
          >
            {accent}
          </Typography>
        )}
      </Box>
      {subtitle && (
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: 11.5,
            color: "#A8A8A8",
            mt: 0.75,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function CutCard({ children, sx, onClick, component = "div" }) {
  return (
    <Box
      component={component}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
      sx={{
        bgcolor: ga.paper,
        border: `1px solid ${ga.border}`,
        clipPath: cutCorner(12),
        display: "block",
        width: "100%",
        textAlign: "left",
        p: 0,
        font: "inherit",
        color: "inherit",
        cursor: onClick ? "pointer" : "default",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function PillChip({ label, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: "14px",
        py: "9px",
        borderRadius: "20px",
        bgcolor: selected ? ga.lime : ga.paper,
        border: selected ? `1px solid ${ga.lime}` : `1px solid ${ga.border}`,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "1.2px",
          color: selected ? ga.bg : ga.muted,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function EggBadge({ size = 15 }) {
  return (
    <Box
      component="img"
      src={eggImg}
      alt=""
      sx={{ width: size, height: size * 1.2, objectFit: "contain" }}
    />
  );
}

export function PlusEgg() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        bgcolor: ga.bg,
        border: `1px solid ${ga.gold}`,
        height: 16,
        px: "6px",
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 9,
          color: ga.gold,
          lineHeight: "11px",
        }}
      >
        +1
      </Typography>
      <EggBadge size={10} />
    </Box>
  );
}

export function LangSwitch({ lang, switchLang }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {["ur", "en"].map((code) => (
        <Box
          key={code}
          component="button"
          type="button"
          aria-label={code === "ur" ? "Switch to Urdu" : "Switch to English"}
          aria-pressed={lang === code}
          onClick={() => switchLang(code)}
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "1px",
            color: lang === code ? ga.bg : ga.muted,
            bgcolor: lang === code ? ga.lime : "transparent",
            px: lang === code ? "11px" : "8px",
            py: "7px",
            borderRadius: "2px",
            cursor: "pointer",
            textTransform: "uppercase",
            border: "none",
            lineHeight: 1.2,
            transition: "all 0.25s ease-in-out",
            "&:hover": {
              color: lang === code ? ga.bg : ga.white,
              bgcolor: lang === code ? ga.lime : "rgba(184, 238, 60, 0.15)",
              transform: lang === code ? "none" : "translateY(-1px)",
              textShadow: lang === code ? "none" : `0 0 8px ${ga.lime}80`,
            },
            "&:active": {
              transform: "scale(0.95)",
            }
          }}
        >
          {code}
        </Box>
      ))}
    </Box>
  );
}

export function EggWalletChip({ count, onClick }) {
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      aria-label={`${count} eggs`}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        minWidth: 91,
        height: 46,
        px: "11px",
        boxSizing: "border-box",
        bgcolor: ga.paper,
        border: `1px solid ${ga.border}`,
        borderRadius: "50px",
        cursor: onClick ? "pointer" : "default",
        p: 0,
      }}
    >
      <Box
        component="img"
        src={eggWalletImg}
        alt=""
        sx={{ width: 22, height: 22 * 1.2, objectFit: "contain" }}
      />
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 22,
          color: ga.gold,
          lineHeight: "31px",
        }}
      >
        {count}
      </Typography>
    </Box>
  );
}

export function ProfileImage({ src, alt = "", sx }) {
  const [failed, setFailed] = useState(false);
  const usingFallback = useRef(false);

  useEffect(() => {
    setFailed(false);
    usingFallback.current = false;
  }, [src]);

  return (
    <Box
      component="img"
      src={!src || failed ? avatarFallback : src}
      alt={alt}
      onError={() => {
        if (usingFallback.current) return;
        usingFallback.current = true;
        setFailed(true);
      }}
      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", ...sx }}
    />
  );
}

export function AvatarButton({ src, onClick, size = 32 }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label="Open profile"
      sx={{
        width: size,
        height: size,
        border: `2px solid ${ga.lime}`,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        overflow: "hidden",
        flexShrink: 0,
        bgcolor: ga.paper,
      }}
    >
      <ProfileImage src={src} />
    </Box>
  );
}

export function HideScrollSx() {
  return {
    "&::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none",
  };
}
